// Created: 2026-08-13
// Updated: 2026-09-07
//
// The single source of key truth for the browse/search section (react-query-browse-search, Req 1).
//
// Inherited conventions (slice 1 `queries/material/keys.ts`, slice 2 `queries/pattern/keys.ts`, slice 3
// `queries/craftProject/keys.ts`): keys are hierarchical arrays of JSON-serializable primitives and
// plain objects, element 0 names the resource, PERSONAL data is namespaced under a leading 'me' and its
// query is always `enabled: !!idToken`. A parameter belongs in a key only when it selects a DIFFERENT
// resource, not when it merely filters one.
//
// NEW in this slice, because slices 1–3 were all detail pages: LIST machinery. Two rules are established
// here and inherited by every later list surface (slice 5 onward).
//
// RULE 1 — normalize BEFORE keying. TanStack Query's `hashKey` already sorts object keys when hashing,
// so `{brand, color}` vs `{color, brand}` is NOT the risk. The real risks, all reachable from
// `MaterialFilterSidebar`, are:
//   - EMPTY-STRING and WHITESPACE-ONLY values. `searchPatterns` appends `materialBrand` only
//     `if (materialFilters?.brand)`, so `{}`, `{brand: ''}`, and `{brand: '   '}` produce the SAME URL
//     but three different hashes — three cache entries for one byte-identical request.
//   - `undefined` values. `JSON.stringify` happens to drop them, so `{brand: undefined}` hashes like
//     `{}` today — but that is an implementation detail of the hasher, not a contract. Normalizing makes
//     it a contract a test can hold.
//   - UNKNOWN keys. A key the transport does not read must not split the cache.
// The same hole exists on the term: `SearchResults` calls `searchPatterns(query || '*', …)`, so `q=''`
// and `q='*'` are ONE request and must be ONE key — that is `normalizeTerm`.
//
// RULE 2 — a cursor NEVER goes in the key. React Query stores every page of one infinite query under a
// SINGLE cache entry, with the cursor in `pageParams`. Putting an offset or a `lastKey` in the key would
// mint one entry per page and break accumulation. See `queries.ts`.

import type { MaterialFilters } from '@mrb91002/shared-types'

/**
 * Stale window for volatile browse LISTS — applied as a per-query override, never as a client default.
 *
 * The shared 5-minute baseline (`DEFAULT_STALE_TIME_MS`) is right for a detail page and WRONG for a
 * search list. OpenSearch indexing is asynchronous — the integration suite carries `waitForPatternIndexed`
 * (`integration-tests/fixtures/waits.ts`) precisely because a just-published pattern is not immediately
 * searchable. Pre-migration every mount refetched, so perceived staleness was zero; a 5-minute window
 * would mean a user who publishes, navigates away, and comes back sees a list missing their own pattern
 * for up to five minutes and blames the site.
 *
 * 30 s keeps the dedup and back-navigation win this slice exists for (scrolling away and back inside one
 * visit is instant, no request) while bounding staleness to well under the human "did that land?" loop.
 *
 * NOT applied to `useBrandMaterialsQuery` — a brand catalogue changes on the order of days, so it keeps
 * the baseline. The rule is TUNE TO VOLATILITY, not always-override (design § D4, Req 5.1–5.4).
 */
export const BROWSE_LIST_STALE_TIME_MS = 30_000

/**
 * The canonical filter object: exactly the four keys `searchPatterns` actually appends to the URL,
 * in a fixed declaration order, with empty values absent rather than present-and-falsy.
 */
export type NormalizedMaterialFilters = {
  brand?: string
  category?: string
  color?: string
  name?: string
}

/** The four filter names the transport reads. Anything else cannot affect the request. */
const KNOWN_FILTER_KEYS = ['brand', 'category', 'color', 'name'] as const

/**
 * Reduce a raw `MaterialFilters` to its canonical form: project onto the four known keys in fixed
 * order, dropping `undefined`, empty-string, and whitespace-only values, and dropping unknown keys
 * (Req 1.3, 1.4, 1.5).
 *
 * Which of the three drops actually bites, in practice:
 *   - `undefined` / EMPTY-STRING: the transport's truthiness guard skips them, so they are the
 *     byte-identical-request case Req 1.4 names.
 *   - WHITESPACE-ONLY: `' '` is truthy, so the transport WOULD append `materialBrand=%20`. It is dropped
 *     anyway because `MaterialFilterSidebar.handleApply` already trims and drops blanks, so no surface
 *     can produce one — and because the hooks pass these NORMALIZED filters to the fetcher (not the raw
 *     object), the key and the URL still agree exactly. That agreement is the invariant; if a surface
 *     ever starts sending a meaningful whitespace-only filter, this collapse must be revisited in the
 *     same change.
 *   - UNKNOWN keys: the one that bites hardest, and it is REACHABLE. `MaterialFilterSidebar.handleApply`
 *     writes category-specific attributes (`weight`, `fiber_content`, `size`, …) into the filter object
 *     via `cleanFilters[attr as keyof MaterialFilters]`, and the transport reads NONE of them. So
 *     `{category: 'yarn', weight: 'worsted'}` and `{category: 'yarn', weight: 'bulky'}` are ONE request
 *     today and must be ONE cache entry.
 *
 * Values are NOT trimmed, only tested for emptiness after trimming: `{brand: ' Red Heart '}` keeps its
 * spaces because the transport would send them verbatim, and trimming here would make the key describe a
 * request that never went out.
 *
 * Idempotent by construction: `normalizeMaterialFilters(normalizeMaterialFilters(x))` equals
 * `normalizeMaterialFilters(x)`, which is what makes it safe to call on an already-normalized object.
 */
export function normalizeMaterialFilters(raw?: MaterialFilters | null): NormalizedMaterialFilters {
  const normalized: NormalizedMaterialFilters = {}
  if (!raw) return normalized

  for (const key of KNOWN_FILTER_KEYS) {
    const value = (raw as Record<string, unknown>)[key]
    if (typeof value !== 'string') continue
    if (value.trim() === '') continue
    normalized[key] = value
  }

  return normalized
}

/**
 * Reduce a raw search term to the term that actually goes on the wire: trimmed, with `''` becoming the
 * `'*'` the surfaces already substitute via `query || '*'` (Req 1.7).
 *
 * So `''`, `'   '`, and `'*'` are ONE key because they are ONE request, while `'cat'` and `'dog'` stay
 * two.
 */
export function normalizeTerm(raw: string | null | undefined): string {
  const trimmed = (raw ?? '').trim()
  return trimmed === '' ? '*' : trimmed
}

type SortByValue = 'newest' | 'highest-rated' | 'most-reviewed'
type DateRangeValue = 'day' | 'week' | 'month' | 'year' | 'all'

/**
 * Every browse/search key in the app. Element 0 names the resource; no other module builds one.
 *
 * `limit` is in every list key because it selects a different list SIZE, not a filtered view of one:
 * this is what keeps the `SearchBar` typeahead (8 items) and the `/search` list (50 items) in two
 * separate entries instead of clobbering each other (Req 1.8). Precedent: slice 2 keys
 * `patternKeys.craftProjects` on `limit` for the same reason.
 *
 * `sortBy`/`dateRange` are likewise IDENTITY, not filters: the backend serves a different ordering AND a
 * different date window — a different result set from a different backend (OpenSearch vs DynamoDB), not
 * a client-side reordering of one list.
 *
 * NO cursor or offset appears anywhere below. See RULE 2 in the module header.
 */
export const browseKeys = {
  /**
   * PUBLIC — Home's newest-patterns list (DynamoDB, cursor-paginated). Seedable for the later SSG/ISR
   * step.
   *
   * Carries NO identity component, and that is established from code, not assumed: `fetchNewestPatternsFull`
   * sends no `Authorization` header, and `lambda-main/src/routes/get-public-patterns-full.ts` reads no
   * authorizer and no caller identity. The same bytes serve every visitor.
   *
   * IF that ever stops holding — an authorizer appears on the public proxy, or the handler starts reading
   * its event — THEN the identity-varying data MUST move under a `['me', …]` key or this key MUST gain an
   * identity discriminator, in the SAME change. Otherwise one shared entry would serve one caller's data
   * to another (Req 4.1, 4.4). The same caveat applies to every other public key in this factory.
   */
  newestPatterns: (limit: number) => ['newestPatterns', { limit }] as const,

  /**
   * PUBLIC — Home's sorted/date-filtered list (OpenSearch, offset-paginated). A DIFFERENT resource from
   * `newestPatterns`, served by a different backend, which is why Home enables exactly one of the two at
   * a time rather than merging them (Req 3.8). Handler: `search-patterns-filtered.ts`, no authorizer.
   */
  patternsFiltered: (sortBy: SortByValue, dateRange: DateRangeValue, limit: number) =>
    ['patternsFiltered', { sortBy, dateRange, limit }] as const,

  /**
   * PUBLIC — pattern search. `q` must arrive through {@link normalizeTerm} and `filters` through
   * {@link normalizeMaterialFilters}; this factory does not normalize, so that the normalization is
   * visible at the call site and testable on its own. Handler: `search-patterns.ts`, no authorizer.
   */
  patternSearch: (q: string, limit: number, filters: NormalizedMaterialFilters) =>
    ['patternSearch', { q, limit, filters }] as const,

  /** PUBLIC — craft-project search. Handler: `search-projects.ts`, no authorizer. */
  projectSearch: (q: string, limit: number) => ['projectSearch', { q, limit }] as const,

  /** PUBLIC — patterns carrying one tag. A tag search is its own resource, not a filter over `patternSearch`. */
  tagSearch: (tag: string, limit: number) => ['tagSearch', { tag, limit }] as const,

  /** PUBLIC — patterns at one difficulty. Same reasoning as `tagSearch`. */
  difficultySearch: (difficulty: string, limit: number) => ['difficultySearch', { difficulty, limit }] as const,

  /**
   * PUBLIC — one brand's whole material catalogue. No `limit`: the response is the entire
   * `{parent_materials, standalone_materials}` payload, so there is no list size to select.
   * Handler: `brand-search.ts`, no authorizer.
   */
  brandMaterials: (brandName: string) => ['brandMaterials', brandName] as const,

  /**
   * PUBLIC — collection search. No `limit` in the key: the surface hardcodes `limit: '20'` and has no
   * reachable second page, so there is no second size to distinguish (design § D1).
   *
   * `searchCollections` lives under `src/api/secure/` but is a PUBLIC fetcher — raw `fetch` against
   * `API_BASE`, and `CollectionSearchPage` passes no token, so the headers are `{}`. Handler:
   * `search-collections.ts`, no authorizer. The folder is misfiled, not a capability.
   */
  collectionSearch: (q: string) => ['collectionSearch', { q }] as const,

  /**
   * TOKEN-GATED — the public-catalog discover browse. The only `'me'`-keyed entry in this factory.
   * Never dehydrated into shared HTML.
   *
   * The `'me'` prefix outlived its original reason and is KEPT for a narrower one. It was chosen
   * because the response varied per caller: the handler filtered `identity_id <> :userId` so the feed
   * excluded the caller's own materials. Since 2026-09-07 the route browses the shared public catalog,
   * so two callers get the SAME list and the response is no longer identity-varying. What has not
   * changed is that `discoverMaterials` sends `Authorization: Bearer ${idToken}` and the handler
   * answers 401 without an identity — so this is a token-gated response, and dehydrating one into
   * shared HTML is the hazard the key guards against (Req 4.2). If the route moves to the
   * unauthenticated service (filed as a backlog row), this entry can become a public key.
   *
   * `category` is normalized to `undefined` for an empty selection so the "All Categories" choice is one
   * entry, matching the transport's `if (category)` guard.
   */
  myMaterialDiscovery: (category: string | undefined, limit: number) =>
    ['me', 'materialDiscovery', { category: category || undefined, limit }] as const,
}

/** The union of every key this factory emits — used by the key tests. */
export type BrowseKey = ReturnType<(typeof browseKeys)[keyof typeof browseKeys]>
